import React, { useState, useEffect } from 'react';
import { ProgramModel } from '../../types/program';
import Modal from '../../Common/Components/Modal';
import { ProgramsService } from '../../services/programsService';
import { Upload } from 'lucide-react';
import { toast } from 'react-toastify';

interface AddProgramDialogProps {
  onClose: () => void;
  onSave: (program: ProgramModel) => Promise<void>;
  show: boolean;
}

const AddProgramDialog: React.FC<AddProgramDialogProps> = ({
  onClose,
  onSave,
  show
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !description.trim() || !url.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a program image');
      return;
    }

    setIsLoading(true);
    try {
      const photoUrl = await ProgramsService.uploadProgramImage(selectedFile);

      const program: ProgramModel = {
        id: '', // Will be set by Firestore
        title: title.trim(),
        description: description.trim(),
        photoUrl,
        url: url.trim(),
        createdAt: new Date(),
      };

      await onSave(program);
      onClose();

      setTitle('');
      setDescription('');
      setUrl('');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error('Failed to save program. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setTitle('');
      setDescription('');
      setUrl('');
      setSelectedFile(null);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} id="addProgramModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Add New Program</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Program Image *</label>
            <div className="relative border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg p-4 cursor-pointer" onClick={() => document.getElementById('program-image-input')?.click()}>
              <div className="text-center">
                {selectedFile ? (
                  <>
                    <div className="size-16 bg-slate-100 rounded-full dark:bg-zink-600 mx-auto mb-2 flex items-center justify-center">
                      <img
                        src={previewUrl || ''}
                        alt="Selected program"
                        className="h-16 w-16 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) nextElement.style.display = 'flex';
                        }}
                      />
                      <div className="size-16 bg-gray-100 rounded-full dark:bg-gray-500/20 items-center justify-center hidden">
                        <span className="text-gray-500 text-2xl">📚</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-zink-300">
                      Click to change image
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="size-8 text-slate-400 dark:text-zink-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-zink-300 mb-2">
                      Click to upload program image
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zink-400">
                      PNG, JPG up to 5MB
                    </p>
                  </>
                )}
              </div>
              <input
                id="program-image-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter program title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500 resize-none"
              placeholder="Enter program description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program URL *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="https://example.com/program"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Create
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddProgramDialog;